/*
 *   Copyright (c) 2024 Andrew Halliwell

 *   This program is free software: you can redistribute it and/or modify
 *   it under the terms of the GNU General Public License as published by
 *   the Free Software Foundation, either version 3 of the License, or
 *   (at your option) any later version.

 *   This program is distributed in the hope that it will be useful,
 *   but WITHOUT ANY WARRANTY; without even the implied warranty of
 *   MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *   GNU General Public License for more details.

 *   You should have received a copy of the GNU General Public License
 *   along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faDownload } from "@fortawesome/free-solid-svg-icons";
import { getMetadata } from "meta-png";
import { EncryptPassword } from "./EncryptPassword";
import { usePostState } from "../stores/stores";
import { Suspense, useEffect, useState, useRef, useLayoutEffect } from "react";
import { handleDecrypt } from "../utils/encryption";
import { useTranslation } from "react-i18next";
import { Spinner } from "flowbite-react/components/Spinner";
import { saveAs } from "file-saver";
import { Link, useNavigate } from 'react-router-dom';
import { Clipboard } from "flowbite-react/components/Clipboard";
import { PostProps } from "../types";
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from "react-toastify";

/**
 * A single post in the feed, displaying the image and allowing the user to input a password to decrypt the image.
 * 
 * @param id The ID of the post
 * @param entryId The ID of the post
 * @param author The author of the post
 * @param posted The date the post was posted
 * @param image The URL of the image in the post
 * @param width The width of the image in the post
 * @param height The height of the image in the post
 * @returns A JSX element representing the post
 */
const Post: React.FC<PostProps> = ({ id, entryId, author, posted, image, width, height }: PostProps) => {

    const { t } = useTranslation();

    const postState = usePostState((state) => state.posts[id]);
    const setPostState = usePostState((state) => state.setPostState);
    const navigate = useNavigate();
    const [contentHeight, setContentHeight] = useState<string | number>(140);
    const imageRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

    // Initialize the state for this post if it doesn't exist
    useEffect(() => {
        if (!postState) {
            setPostState(id, {
                input: "",
                password: "",
                output: [],
                image: image as string,
                width: width,
                height: height,
                encryptedText: "",
                decryptedText: "",
                author: author,
                stringToDecrypt: "",
                encryptionEnabled: false,
            });
        }
    }, [id, image, author, postState, setPostState, width, height]);

    useLayoutEffect(() => {
        if (imageRef.current) {
            const updateContentHeight = () => {
                const newHeight = imageRef.current?.scrollHeight;
                if (newHeight) {
                    setContentHeight(newHeight);
                }
            };

            updateContentHeight();

            window.addEventListener('resize', updateContentHeight);

            return () => {
                window.removeEventListener('resize', updateContentHeight);
            };
        }
    }, [imageRef]);

    useEffect(() => {
        if (imageRef.current || postState?.decryptedText) {
            const updateContentHeight = () => {
                const newHeight = imageRef.current?.scrollHeight;
                if (newHeight) {
                    setContentHeight(newHeight);
                }
            };

            updateContentHeight();
        }

    }, [imageRef, postState?.decryptedText]);

    useEffect(() => {
        const handleResize = () => {
            if (imageRef.current) {
                const { width, height } = imageRef.current.getBoundingClientRect();
                setDimensions({ width, height });
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [imageRef]);

    useEffect(() => {
        if (dimensions.width !== 0 && dimensions.height !== 0) {
            const updateContentHeight = () => {
                const newHeight = imageRef.current?.scrollHeight;
                if (newHeight) {
                    setContentHeight(newHeight);
                }
            };

            updateContentHeight();
        }
    }, [dimensions, imageRef]);

    const convertUrlToUint8Array = async (url: string) => {
        const response = await fetch(url);
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const imageArray = new Uint8Array(arrayBuffer);
        return imageArray;
    };

    useEffect(() => {
        const convertImage = async () => {
            const imageArray = await convertUrlToUint8Array(postState?.image as string);
            const stringToDecrypt = getMetadata(imageArray, "Message");
            if (postState?.password && stringToDecrypt) {
                handleDecrypt(
                    stringToDecrypt,
                    postState.password,
                    (decrypted) => setPostState(id, { decryptedText: decrypted }),
                    (message) => toast.error(message)
                );
            }
        };
        if (postState?.password) convertImage();
    }, [postState?.password, id, postState?.image, setPostState]);

    const handleImageDownload = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        if (postState?.image) {
            const url = postState?.image;
            const filename = url.split("/").pop();
            if (filename) {
                const response = await fetch(url);
                const blob = await response.blob();
                saveAs(blob, filename);
            } else {
                console.log("Failed to get filename");
            }
        } else {
            console.log("No image to download");
        }
    };

    const handleLinkClick = (event: React.MouseEvent<HTMLInputElement>) => {
        event.preventDefault();
        navigate(`/${entryId}`);
    };

    const LinkIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-auto" fill="currentColor" stroke="currentColor" viewBox="0 0 640 512"><path d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z" /></svg>
    );

    return (
        <div className="pb-4" id={entryId} key={id}>
            <div className="flex flex-shrink-0 pb-0">
                <div className="flex items-top bg-gray-50 dark:bg-slate-900 p-4 xs:pb-0 rounded-t-lg">
                    <div className="px-3 xs:px-0">
                        <div className="items-center text-left text-base leading-6 font-medium text-gray-900 dark:text-white">
                            <span className="text-gray-900 dark:text-white xs:flex items-center">{author}<FontAwesomeIcon className="mx-2" icon={faUser} /></span>
                            <span className="dark:text-white text-sm xs:text-xs leading-5 font-medium text-gray-700 hover:text-gray-300 transition ease-out duration-150">{t('post.posted')} {new Date(posted).toDateString()}
                            </span>
                            <br />
                            <div className="pt-3">
                                <span className="text-xs dark:text-gray-300 leading-5 font-medium text-gray-500 hover:text-gray-300 transition ease-out duration-150">
                                    <div className="grid w-40">
                                        <div className="relative">
                                            <label htmlFor="post-link" className="sr-only">
                                                ID:<Link to={"https://stegg.alifeinbinary.com/#/" + entryId} className="mx-2 hover:underline">{entryId}</Link>
                                            </label>
                                            <input
                                                id="post-link"
                                                type="button"
                                                className="cursor-pointer col-span-6 block rounded-lg border border-gray-300 bg-gray-50 hover:underline py-2 px-2.5 text-sm text-gray-500 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-400 dark:placeholder:text-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500 w-full text-left"
                                                value={entryId}
                                                readOnly
                                                onClick={handleLinkClick}
                                            />
                                            <Clipboard.WithIconText label="Copy link" icon={LinkIcon} className="py-1 dark:bg-gray-600 bg-gray-300 hover:bg-gray-200" valueToCopy={`https://stegg.alifeinbinary.com/#/${entryId}`} />
                                        </div>
                                    </div>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <div className="min-h-max">
                    <motion.div
                        key={entryId}
                        initial={{ height: 'auto' }}
                        animate={{ height: contentHeight }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        style={{
                            overflow: 'hidden',
                            transitionProperty: 'height',
                            transitionDuration: '0.3s',
                            transitionTimingFunction: 'ease-in-out',
                        }}
                    >
                        <AnimatePresence mode='wait'>
                            <motion.div
                                key={entryId}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div ref={imageRef} className="flex items-center justify-center pt-5 pb-3 px-7 xs:px-4 xs:pt-5 xs:pb-0 bg-gray-50 xs:min-h-[140px] h-full dark:bg-slate-900 rounded-tr-lg transition-colors duration-300">
                                    <div className="p-5 dark:hover:bg-slate-800 rounded-lg transition-colors duration-300">
                                        {!postState?.image ? <Spinner color="gray" aria-label="Loading" /> : null}
                                        {postState?.decryptedText ? (
                                            <p className="flex items-center justify-center break-words text-wrap text-xl text-left w-full font-medium text-gray-900 dark:text-white flex-shrink pb-10 min-h-[415px]" style={{ overflowWrap: "anywhere" }}>
                                                {postState.decryptedText}
                                            </p>
                                        ) : (
                                            <Suspense fallback={<Spinner color="gray" aria-label="Loading" />}>
                                                <img className="remove-watermark" src={postState?.image} alt={"Binary image " + entryId} width={postState?.width} height={postState?.height} />
                                            </Suspense>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </motion.div>
                </div>
                <div className="flex justify-between gap-2">
                    <div className="flex-shrink-0 bg-gray-50 dark:bg-slate-900 px-3 py-2 rounded-bl-lg">
                        <div className="flex items-center">
                            <button aria-label="Download image" title="Download image" className="inline-flex h-10 w-10 items-center justify-center transition ease-in-out duration-300 cursor-pointer text-sm font-medium text-center rounded-lg bg-gray-200 dark:bg-slate-700 hover:ring-transparent text-gray-700 dark:text-gray-300 hover:bg-sagegreen hover:text-white dark:hover:bg-sagegreen focus:ring-blue-200 focus:ring-4 active:scale-95" onClick={handleImageDownload}>
                                <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 flex items-center justify-end max-w-72 bg-gray-50 dark:bg-slate-900 px-3 py-2 rounded-br-lg">
                        <EncryptPassword
                            password={postState?.password || ""}
                            setPassword={(value) => setPostState(id, { password: value })}
                            encryptionEnabled={postState?.encryptionEnabled || false}
                            setEncryptionEnabled={(value) => setPostState(id, { encryptionEnabled: value })}
                            decryptedText={postState?.decryptedText || ""}
                            setDecryptedText={(value) => setPostState(id, { decryptedText: value })}
                            setOutput={(value) => setPostState(id, { output: value })}
                            stringToDecrypt={postState?.stringToDecrypt || ""}
                            setStringToDecrypt={(value) => setPostState(id, { stringToDecrypt: value })}
                            setEncryptedText={(value) => setPostState(id, { encryptedText: value })}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Post;
